<template>
  <div class="ui-widget-container">
    <div class="row com-mod-module-header-container portfolio-management">
      <h3 class="col-lg-6 col-md-6 com-mod-module-header app-title">
        {{ title }}
        <disclosure-superscript
          class="detail-page"
          anchor="portfolioManagementName"
          :statistics="[getPortfolioManagementName]"
        />
      </h3>
      <span
        v-if="isAuthorizedModule['portfolioManagementLearnLink']"
		id="_portfolioManagementLearnLink"
        class="capital-system-link col-lg-6 col-md-6"
      >
        <a
          class="com-mod-utility-link"
          target="_blank"
          href="https://www.capitalgroup.com/advisor/about-us/capital-system.html"
        >
          <i class="utility ARROW_LINK_icon_16pxpng" />LEARN ABOUT THE CAPITAL SYSTEM<sup>SM</sup>
        </a>
      </span>
    </div>
    <div class="row chart-legend portfolio-management-legend">
      <div class="col-md-12">
        <div class="chart-legend-item label-with-icon">
          <div class="portfolio-legend-item-symbol pull-left" />
          <span class="com-mod-table-label-small wrap-inline">Years of Experience with Fund</span>
        </div>
        <div class="chart-legend-item label-with-icon">
          <div class="portfolio-legend-item-symbol pull-left" />
          <span class="com-mod-table-label-small wrap-inline">Years of Experience with Capital Group</span>
        </div>
        <div class="chart-legend-item label-with-icon">
          <div class="portfolio-legend-item-symbol pull-left" />
          <span class="com-mod-table-label-small wrap-inline">Years of Experience with Investment Industry</span>
        </div>
      </div>
    </div>
    <div class="experience-visualization">
      <div
        v-for="(manager, index) in portfolioData"
        :key="index"
        class="manager-experience-row"
      >
        <span class="manager">{{ manager.displayName }}</span>
        <span class="experience">
          <span class="bar">
            {{ manager.fundExperience }}
          </span>
          <span class="bar">
            {{ manager.cgExperience }}
          </span>
          <span class="bar">
            {{ manager.industryExperience }}
          </span>
          <span class="bar" />
        </span>
      </div>
    </div>
    <div
      class="row"
      style="margin-top: 10px;"
    >
      <div class="col-sm-12">
        <span class="sub-info">A boldface number indicates that years of experience with Capital Group is equal to years of experience with investment industry.</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
export default {
	name: 'PortfolioManagement',
	props: {
		portfolioData: {
			type: Array,
			default: null
		},
		fundObjective: {
			type: String,
			default: ''
		}
	},
	computed: {
		...mapGetters(['getBreakpoint']),
		getPortfolioManagementName() {
			const fundObjectiveToLower = this.fundObjective.toLowerCase();
			if (fundObjectiveToLower.includes('portfolioseries') || fundObjectiveToLower.includes('managedrisk')) return 'PSC_N';
			else if (fundObjectiveToLower.includes('targetdate')) return 'TDSC_N';
			return 'portMgrName';
		},
		title() {
			const fundObjective = this.fundObjective.toLowerCase();
			if (fundObjective === 'portfolioseries' || fundObjective === 'managedrisk') return 'Portfolio Solutions Committee';
			else if (fundObjective === 'retirementtargetdate') return 'Target Date Solutions Committee';
			return 'Portfolio Management';
		}
	},
	watch: {
		portfolioData() {
			this.emitDataToDownload();
		}
	},
	mounted() {
		this.setBarWidths(this.portfolioData);
	},
	methods: {
		emitDataToDownload() {
			const title = this.title;
			const managers = this.portfolioData;
			const data = { managers, title };
			this.$EventBus.$emit('ExcelData', 'PortfolioManagementModule', data);
			this.setBarWidths(managers);
		},
		getExperienceStyle(mgr) {
			let classStyle = '';
			if (mgr.cgExperience === mgr.industryExperience) {
				classStyle += 'pull-right com-mod-table-data experience-cell-bold strong';
			}
			return `${classStyle} pull-right com-mod-table-data`;
		},
		setBarWidths(mgrs) {
			const industryLevelMax = Math.max(...mgrs.map(o => {
				return o.industryExperience;
			}));
			this.$nextTick(() => {
				const managers = [...document.querySelectorAll('.manager-experience-row')];
				//loop over each row
				managers.forEach((managerBarsColection, mgrIndex) => {
					let previousFlex = 0;
					//loop over the bars that visualize the years of experience
					[...managerBarsColection.children[1].children].forEach((el, index) => {
						//setting flex value allows for the bars to fill available space without overlapping
						//The alternative was to set width as a % but values could still overlap
						if (index === 0) {
							el.style['flex'] = industryLevelMax - (industryLevelMax - mgrs[mgrIndex].fundExperience);
							previousFlex += industryLevelMax - (industryLevelMax - mgrs[mgrIndex].fundExperience);
						} else if (index === 1) {
							el.style['flex'] = industryLevelMax - (industryLevelMax - mgrs[mgrIndex].cgExperience) - previousFlex;
							previousFlex += industryLevelMax - (industryLevelMax - mgrs[mgrIndex].cgExperience) - previousFlex;
							//if the years of CG experience is that same as the number of years on fund, bold the text and only render one of the elements (since the values match, there is no need to have two display with the same value)
							if (mgrs[mgrIndex].cgExperience === mgrs[mgrIndex].fundExperience) {
								el.previousElementSibling.style['font-weight'] = 'bold';
								el.style.display = 'none';
							}
						} else if (index === 2) {
							el.style['flex'] = industryLevelMax - (industryLevelMax - mgrs[mgrIndex].industryExperience) - previousFlex;
							previousFlex += industryLevelMax - (industryLevelMax - mgrs[mgrIndex].industryExperience) - previousFlex;
							if (mgrs[mgrIndex].industryExperience === mgrs[mgrIndex].cgExperience) {
								el.previousElementSibling.style['font-weight'] = 'bold';
								el.style.display = 'none';
							}
						} else if (index === 3) {
							//have a 4th bar fill the remaingin space so that all bars width are a ratio relative to the max years between all manages
							el.style['flex'] = industryLevelMax - previousFlex;
						}
					});
				});
			});
		}
	}
};
</script>

<style lang="less" scoped>
	#afis-app {
		.bold {
			font-weight: bold;
		}
		a.com-mod-utility-link {
			// Add padding to give room for the 'SM' when added to the link. 14px matches other links on the site.
			padding-right: 14px;
			// Override bootstrap superscript font size from 10.5px to 9px
			sup {
			font-size: 9px;
			}
		}
		.portfolio-management-legend{
			.portfolio-legend-item-symbol{
				display: inline-block;
				padding-bottom: 0;
				padding-top: 0;
				border: 0 none;
				margin-bottom: 0;
				margin-top: 5px;
				margin-right: 5px;
				width: 10px;
				height: 10px;
			}
			.chart-legend-item:nth-of-type(1) .portfolio-legend-item-symbol {
				background-color: #CCCCCC
			}
			.chart-legend-item:nth-of-type(2) .portfolio-legend-item-symbol {
				background-color: #E4E4E4
			}
			.chart-legend-item:nth-of-type(3) .portfolio-legend-item-symbol {
				background-color: #F5F5F5
			}
		}
		.capital-system-link {
			float: right;
			margin-top: 13px;
			@media (max-width: 480px) {
				float: left;
				margin: 0px 0px 10px 0px;
			}
		}
		.experience-visualization {
			.manager-experience-row {
				display: flex;
				.manager {
					width: 20%;
					color: #666;
					font-family: AvenirNextLTW01-Regular,Arial;
					font-size: 12px;
					font-weight: 400;
					border-top: 1px solid #d4d4d4;
					border-right: 20px solid #fff;
					line-height: 20px;
				}
				.experience {
					width: 80%;
					display: flex;
				}
				.bar {
					display: inline-block;
					text-align: right;
					padding: 0 5px;
					&:nth-of-type(1) {
						background-color: #CCCCCC;
					}
					&:nth-of-type(2) {
						background-color: #E4E4E4;
					}
					&:nth-of-type(3) {
						background-color: #F5F5F5;
					}
				}
			}
		}
		@media (max-width: 768px) {
			.experience-visualization {
				display: table;
				width: 100%;
				.manager-experience-row {
					display: table-row;
					&:first-of-type {
						.manager {
							border-top: 1px solid #ccc;
						}
					}
					.manager {
						width: 60%;
						display: table-cell;
						border-bottom: 1px solid #ccc;
						font-size: 14px;
					}
					.experience {
						display: contents;
						.bar {
							width: 33%;
							text-align: center;
							padding: 4px;
							border: 1px solid #fff;
							&:last-of-type {
								display: none;
							}
						}
					}
				}
			}
		}
	}
</style>
